/**
 * /erp/orders/new — create a draft order (ERP v1.2).
 *
 * Header: optional code + notes, plus CRM links (deal / account /
 * contact) that may be pre-filled from search params when arriving via
 * "Convert to order" on a deal. Lines: product + qty + unit price +
 * a "custom (needs production)" toggle.
 *
 * Creates the order in ``draft``; on success redirects to the detail
 * page where the manager submits / marks ready / dispatches.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Loader2, Plus, ShoppingCart, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import { AccountPicker } from "../components/AccountPicker";
import { ContactPicker } from "../components/ContactPicker";
import {
  ApiError,
  type ErpOrderCreate,
  type ErpOrderLineWrite,
  accountsApi,
  contactsApi,
  erpApi,
} from "../lib/api";
import { useItemAvailability, useProducts, useUoms } from "../lib/erp";
import { toast } from "../lib/toast";
import { formatQty, sanitizeQtyInput } from "../lib/units";

import type { ErpItemAvailability } from "../lib/api";

function AvailabilityHint({
  productId,
  avail,
  uom,
}: {
  productId: string;
  avail: Map<string, ErpItemAvailability>;
  uom?: string | undefined;
}) {
  const a = avail.get(productId);
  if (!a) return null;
  const available = Number(a.available);
  return (
    <p className="mt-1 text-[11px] opacity-70">
      In stock <span className="font-medium tabular-nums">{formatQty(a.on_hand, uom)}</span> ·
      booked <span className="font-medium tabular-nums">{formatQty(a.booked, uom)}</span> ·
      available{" "}
      <span
        className={`font-medium tabular-nums ${available < 0 ? "text-amber-600 dark:text-amber-400" : ""}`}
      >
        {formatQty(a.available, uom)}
      </span>
    </p>
  );
}

interface OrdersNewSearch {
  deal_id?: string | undefined;
  account_id?: string | undefined;
  contact_id?: string | undefined;
}

export const Route = createFileRoute("/_app/erp/orders/new")({
  validateSearch: (search: Record<string, unknown>): OrdersNewSearch => ({
    deal_id: typeof search.deal_id === "string" ? search.deal_id : undefined,
    account_id: typeof search.account_id === "string" ? search.account_id : undefined,
    contact_id: typeof search.contact_id === "string" ? search.contact_id : undefined,
  }),
  component: NewOrderPage,
});

interface DraftLine {
  key: string;
  product_id: string;
  custom_name: string;
  custom_uom: string;
  qty: string;
  unit_price: string;
  is_custom: boolean;
}

function blankLine(): DraftLine {
  return {
    key: crypto.randomUUID(),
    product_id: "",
    custom_name: "",
    custom_uom: "",
    qty: "1",
    unit_price: "",
    is_custom: false,
  };
}

function NewOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const productsQ = useProducts();
  const availabilityQ = useItemAvailability();
  const uomsQ = useUoms();
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });

  const [code, setCode] = useState("");
  const [accountId, setAccountId] = useState<string>(search.account_id ?? "");
  const [contactId, setContactId] = useState<string>(search.contact_id ?? "");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (body: ErpOrderCreate) => erpApi.orders.create(body),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "orders"] });
      toast.success(`Order ${order.code} created`);
      navigate({ to: "/erp/orders/$orderId", params: { orderId: order.id } });
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.message : "Could not create order");
    },
  });

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function onProductPick(key: string, productId: string) {
    const product = productsQ.data?.items.find((p) => p.id === productId);
    setLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? {
              ...l,
              product_id: productId,
              // Default price to the product MRP when the field is blank.
              unit_price:
                l.unit_price.trim() === "" && product?.default_sell_price
                  ? product.default_sell_price
                  : l.unit_price,
            }
          : l,
      ),
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    const usable = lines.filter((l) =>
      l.is_custom ? l.custom_name.trim() && Number(l.qty) > 0 : l.product_id && Number(l.qty) > 0,
    );
    if (usable.length === 0) {
      setFormError(
        "Add at least one line: pick a product (or name a custom item) with a positive quantity.",
      );
      return;
    }

    if (!accountId && !contactId) {
      setFormError("An order must have a contact or an account.");
      return;
    }

    const orderLines: ErpOrderLineWrite[] = usable.map((l, idx) =>
      l.is_custom
        ? {
            item_kind: "custom",
            custom_name: l.custom_name.trim(),
            custom_uom: l.custom_uom.trim() || null,
            qty: l.qty,
            unit_price: l.unit_price.trim() ? l.unit_price : null,
            is_custom: true,
            position: idx,
          }
        : {
            item_kind: "product",
            product_id: l.product_id,
            qty: l.qty,
            unit_price: l.unit_price.trim() ? l.unit_price : null,
            is_custom: false,
            position: idx,
          },
    );

    createMut.mutate({
      code: code.trim() || null,
      deal_id: search.deal_id ?? null,
      account_id: accountId || null,
      contact_id: contactId || null,
      notes: notes.trim() || null,
      lines: orderLines,
    });
  }

  const products = productsQ.data?.items ?? [];
  const availById = new Map((availabilityQ.data?.items ?? []).map((a) => [a.product_id, a]));

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <div>
        <Link
          to="/erp/orders"
          className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="size-4" aria-hidden /> Back to orders
        </Link>
      </div>

      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
        <ShoppingCart className="size-5 opacity-70" aria-hidden />
        New order
      </h1>

      {(search.deal_id || search.account_id || search.contact_id) && (
        <p className="rounded-md border border-[var(--border)] bg-black/[0.02] px-3 py-2 text-xs opacity-70 dark:bg-white/[0.02]">
          This order will be linked to the record you came from.
        </p>
      )}

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Auto-generated if blank (ORD-…)"
              maxLength={32}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <span className="text-xs font-medium opacity-80">
              Account <span className="opacity-50">(account or contact required)</span>
            </span>
            <AccountPicker
              value={accountId || null}
              onChange={(next) => {
                setAccountId(next ?? "");
                // Drop a contact that no longer belongs to the new account.
                if (
                  next &&
                  contactId &&
                  contactsQ.data?.items.find((c) => c.id === contactId)?.account_id !== next
                ) {
                  setContactId("");
                }
              }}
              accounts={accountsQ.data?.items ?? []}
              placeholder="Pick an account…"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium opacity-80">Contact</span>
            <ContactPicker
              value={contactId}
              onChange={(next) => setContactId(next)}
              contacts={
                accountId
                  ? (contactsQ.data?.items ?? []).filter(
                      (c) => c.account_id === accountId || c.id === contactId,
                    )
                  : (contactsQ.data?.items ?? [])
              }
              placeholder="Pick a contact…"
            />
          </div>
        </div>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything the team needs to know about this order."
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Lines</h2>
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, blankLine()])}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Plus className="size-3" aria-hidden /> Add line
            </button>
          </div>

          <div className="space-y-2">
            {lines.map((l) => (
              <div
                key={l.key}
                className="grid grid-cols-1 gap-2 rounded-lg border border-[var(--border)] p-3 sm:grid-cols-[1fr_5rem_6rem_auto_auto]"
              >
                <div className="space-y-1">
                  <span className="text-[11px] font-medium opacity-70 sm:hidden">
                    {l.is_custom ? "Custom item" : "Product"}
                  </span>
                  {l.is_custom ? (
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        value={l.custom_name}
                        onChange={(e) => updateLine(l.key, { custom_name: e.target.value })}
                        placeholder="Describe the custom item…"
                        maxLength={200}
                        aria-label="Custom item name"
                        className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
                      />
                      <select
                        value={l.custom_uom}
                        onChange={(e) => updateLine(l.key, { custom_uom: e.target.value })}
                        aria-label="Unit"
                        className="w-24 shrink-0 rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
                      >
                        <option value="">uom…</option>
                        {(uomsQ.data?.items ?? []).map((u) => (
                          <option key={u.code} value={u.code}>
                            {u.code}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <select
                      value={l.product_id}
                      onChange={(e) => onProductPick(l.key, e.target.value)}
                      aria-label="Product"
                      className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
                    >
                      <option value="">— pick product —</option>
                      {products
                        .filter((p) => p.is_active)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.code} — {p.name}
                          </option>
                        ))}
                    </select>
                  )}
                  {!l.is_custom && l.product_id && (
                    <AvailabilityHint
                      productId={l.product_id}
                      avail={availById}
                      uom={products.find((p) => p.id === l.product_id)?.uom}
                    />
                  )}
                </div>
                <label className="space-y-1">
                  <span className="text-[11px] font-medium opacity-70 sm:hidden">Qty</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={l.qty}
                    onChange={(e) =>
                      updateLine(l.key, {
                        qty: sanitizeQtyInput(
                          e.target.value,
                          l.is_custom
                            ? l.custom_uom
                            : products.find((p) => p.id === l.product_id)?.uom,
                        ),
                      })
                    }
                    aria-label="Quantity"
                    className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] font-medium opacity-70 sm:hidden">Unit price</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={l.unit_price}
                    onChange={(e) => updateLine(l.key, { unit_price: e.target.value })}
                    placeholder="price"
                    aria-label="Unit price"
                    className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
                  />
                </label>
                <label className="flex items-center gap-1.5 self-center text-xs">
                  <input
                    type="checkbox"
                    checked={l.is_custom}
                    onChange={(e) => updateLine(l.key, { is_custom: e.target.checked })}
                  />
                  Custom
                </label>
                <button
                  type="button"
                  onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                  className="self-center rounded-md border border-[var(--border)] p-1.5 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                  aria-label="Remove line"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[11px] opacity-60">
            Tick <strong>Custom</strong> for items that need production first — the order will go to{" "}
            <em>pending production</em> until a manager marks it ready.
          </p>
        </section>

        {formError && (
          <p className="flex items-center gap-1.5 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="size-4" aria-hidden /> {formError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/erp/orders"
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={createMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {createMut.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
            Create order
          </button>
        </div>
      </form>
    </div>
  );
}
